import { useEffect } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, Star, X } from 'lucide-react';
import { initialsOf } from '@/lib/format';
import { statusMeta } from '@/lib/domain';
import styles from './ui.module.css';

/* ── Page header with a back affordance ──────────────────────────────────── */

export function PageHeader({
  title,
  subtitle,
  back,
  actions,
}: {
  title: string;
  subtitle?: string;
  /** Path to navigate to, or `true` for browser history back. */
  back?: string | true;
  actions?: ReactNode;
}) {
  const navigate = useNavigate();

  return (
    <header className={styles.pageHeader}>
      {back ? (
        <button
          type="button"
          className={styles.backBtn}
          onClick={() => (back === true ? navigate(-1) : navigate(back))}
          aria-label="Retour"
        >
          <ChevronLeft size={22} />
        </button>
      ) : null}
      <div className={styles.pageHeaderText}>
        <h1 className={styles.pageHeaderTitle}>{title}</h1>
        {subtitle ? <p className={styles.pageHeaderSubtitle}>{subtitle}</p> : null}
      </div>
      {actions ? <div className={styles.pageHeaderActions}>{actions}</div> : null}
    </header>
  );
}

/* ── Hero header used by the list screens (brand band) ───────────────────── */

export function HeroHeader({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children?: ReactNode;
}) {
  return (
    <div className={styles.hero}>
      <div className={styles.heroInner}>
        <h1 className={styles.heroTitle}>{title}</h1>
        {subtitle ? <p className={styles.heroSubtitle}>{subtitle}</p> : null}
        {children}
      </div>
    </div>
  );
}

/* ── Avatar ──────────────────────────────────────────────────────────────── */

export function Avatar({
  name,
  src,
  size = 56,
  tone = 'blue',
}: {
  name?: string | null;
  src?: string | null;
  size?: number;
  tone?: 'blue' | 'orange' | 'navy';
}) {
  if (src) {
    return (
      <img
        className={styles.avatarImg}
        style={{ width: size, height: size }}
        src={src}
        alt={name ?? ''}
      />
    );
  }
  return (
    <span
      className={`${styles.avatar} ${styles[`avatar_${tone}`]}`}
      style={{ width: size, height: size, fontSize: Math.round(size * 0.38) }}
      aria-hidden="true"
    >
      {initialsOf(name)}
    </span>
  );
}

/* ── Status badge ────────────────────────────────────────────────────────── */

export function StatusBadge({ status, icon }: { status: string; icon?: ReactNode }) {
  const meta = statusMeta(status);
  return (
    <span className={styles.statusBadge} style={{ '--status': meta.color } as CSSProperties}>
      {icon ?? <span className={styles.statusDot} aria-hidden="true" />}
      {meta.label}
    </span>
  );
}

export function SoftBadge({ status }: { status: string }) {
  const meta = statusMeta(status);
  return (
    <span className={styles.softBadge} style={{ '--status': meta.color } as CSSProperties}>
      {meta.label}
    </span>
  );
}

/* ── Star rating ─────────────────────────────────────────────────────────── */

const STAR_ON = '#FFB21A';
const STAR_OFF = 'var(--navy-line)';

export function Stars({
  value,
  size = 16,
  onChange,
}: {
  value: number;
  size?: number;
  onChange?: (next: number) => void;
}) {
  return (
    <span className={styles.stars}>
      {[1, 2, 3, 4, 5].map((star) =>
        onChange ? (
          <button
            key={star}
            type="button"
            className={styles.starBtn}
            onClick={() => onChange(star)}
            aria-label={`${star} étoile${star > 1 ? 's' : ''}`}
            aria-pressed={star <= value}
          >
            <Star
              size={size}
              color={star <= value ? STAR_ON : STAR_OFF}
              fill={star <= value ? STAR_ON : 'none'}
            />
          </button>
        ) : (
          <Star
            key={star}
            size={size}
            color={star <= value ? STAR_ON : STAR_OFF}
            fill={star <= value ? STAR_ON : 'none'}
          />
        )
      )}
    </span>
  );
}

/* ── Modal ───────────────────────────────────────────────────────────────── */

export function Modal({
  open,
  onClose,
  title,
  children,
  wide,
}: {
  open: boolean;
  onClose: () => void;
  title?: string;
  children: ReactNode;
  wide?: boolean;
}) {
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previous;
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className={styles.modalOverlay} role="dialog" aria-modal="true" onClick={onClose}>
      <div
        className={`${styles.modalCard} ${wide ? styles.modalCardWide : ''}`}
        onClick={(event) => event.stopPropagation()}
      >
        <div className={styles.modalHead}>
          {title ? <h2 className={styles.modalTitle}>{title}</h2> : <span />}
          <button type="button" className={styles.modalClose} onClick={onClose} aria-label="Fermer">
            <X size={18} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

/* ── Full-bleed image lightbox ───────────────────────────────────────────── */

export function Lightbox({ src, onClose }: { src: string | null; onClose: () => void }) {
  useEffect(() => {
    if (!src) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [src, onClose]);

  if (!src) return null;

  return (
    <div className={styles.lightbox} role="dialog" aria-modal="true" onClick={onClose}>
      <button type="button" className={styles.lightboxClose} onClick={onClose} aria-label="Fermer">
        <X size={26} />
      </button>
      <img src={src} alt="" className={styles.lightboxImg} />
    </div>
  );
}

/* ── Empty state ─────────────────────────────────────────────────────────── */

export function EmptyState({
  icon,
  illustration,
  title,
  text,
  action,
}: {
  icon?: ReactNode;
  /** Path to a 3D illustration; takes the place of the icon when given. */
  illustration?: string;
  title: string;
  text?: string;
  action?: ReactNode;
}) {
  return (
    <div className={styles.empty}>
      {illustration ? (
        <img className={styles.emptyIllustration} src={illustration} alt="" />
      ) : icon ? (
        <div className={styles.emptyIcon}>{icon}</div>
      ) : null}
      <h2 className={styles.emptyTitle}>{title}</h2>
      {text ? <p className={styles.emptyText}>{text}</p> : null}
      {action}
    </div>
  );
}
